import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, List, ListItem, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type AgentHelpTopic =
  | 'creation-mode'
  | 'template-selection'
  | 'basic-info'
  | 'brain'
  | 'prompts'
  | 'ai-actions'
  | 'schedule'
  | 'settings'
  | 'review';

type AgentHelpModalProps = {
  open: boolean;
  topic: AgentHelpTopic;
  onClose: () => void;
};

const titles: Record<AgentHelpTopic, string> = {
  'creation-mode': 'Ajuda: Como criar seu agente',
  'template-selection': 'Ajuda: Seleção de Template',
  'basic-info': 'Ajuda: Informações Básicas',
  'brain': 'Ajuda: Cérebro do Agente',
  'prompts': 'Ajuda: Prompts/Treinamentos',
  'ai-actions': 'Ajuda: Ações de IA',
  'schedule': 'Ajuda: Agendamento Randômico',
  'settings': 'Ajuda: Configurações Avançadas',
  'review': 'Ajuda: Revisão Final'
};

const ContentByTopic: React.FC<{ topic: AgentHelpTopic }> = ({ topic }) => {
  switch (topic) {
    case 'creation-mode':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Escolha como deseja iniciar a criação do agente. Existem três caminhos:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Criar do zero"
                secondary="Controle total das etapas. Indicado para quem quer personalização máxima."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Escolher template"
                secondary="Comece com um modelo pré-configurado e ajuste conforme a sua necessidade. Ótimo para ganhar velocidade."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Duplicar de outro agente"
                secondary="Copie configurações que já funcionam em outro agente e adapte."
              />
            </ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Boas práticas</Typography>
          <Typography variant="body2">
            Se for seu primeiro agente, comece por um template. Para cenários específicos, prefira criar do zero.
          </Typography>
        </Box>
      );
    case 'template-selection':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Templates aceleram a configuração inicial. Após escolher, você poderá alterar qualquer campo.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Dicas</Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Alinhe o template ao objetivo" secondary="Qualificação, suporte ou campanhas têm estratégias diferentes." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Revise o modelo de IA" secondary="Escolha um modelo estável e econômico para volume; use um mais potente quando precisão for essencial." />
            </ListItem>
          </List>
        </Box>
      );
    case 'basic-info':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Defina nome, status, modelo de IA, tipo de produto e o delay de resposta.
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Nome" secondary="Use algo claro para os administradores identificarem o agente." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Status" secondary="Ativo para operar; Inativo para testes ou pausa." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Modelo" secondary="Equilibre custo e qualidade (ex.: GPT-4o Mini para custo; modelos mais potentes para qualidade)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tipo de Produto" secondary="Lead, Assistente ou Ambos, conforme o uso esperado." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Delay de Resposta" secondary="Simula digitação humana. Comece com 100–300ms para parecer natural." />
            </ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Como melhorar</Typography>
          <Typography variant="body2">Mantenha nomes consistentes e escolha o modelo com base no volume e precisão desejada.</Typography>
        </Box>
      );
    case 'brain':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Descreva o comportamento, conhecimento e objetivos do agente. Este texto guia todas as respostas.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Estrutura sugerida</Typography>
          <List dense>
            <ListItem><ListItemText primary="Papel" secondary="Quem é o agente e como deve se portar." /></ListItem>
            <ListItem><ListItemText primary="Domínio" secondary="Quais assuntos conhece e quais limitações tem." /></ListItem>
            <ListItem><ListItemText primary="Objetivos" secondary="Resultados esperados (ex.: qualificar, converter, apoiar)." /></ListItem>
            <ListItem><ListItemText primary="Tom e estilo" secondary="Formalidade, empatia, brevidade." /></ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Como melhorar</Typography>
          <Typography variant="body2">Seja específico. Evite generalidades. Use exemplos de boas e más respostas.</Typography>
        </Box>
      );
    case 'prompts':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Adicione prompts de treinamento para orientar respostas em casos específicos.
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Objetividade" secondary="Cada prompt deve ter um propósito claro." /></ListItem>
            <ListItem><ListItemText primary="Contexto" secondary="Inclua restrições, exemplos e formato de saída." /></ListItem>
            <ListItem><ListItemText primary="Iteração" secondary="Teste, colete conversas reais e refine." /></ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Como melhorar</Typography>
          <Typography variant="body2">Prefira instruções curtas e prescritivas; evite ambiguidade e excesso de regras conflitantes.</Typography>
        </Box>
      );
    case 'ai-actions':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Configure ações de IA que disparam mensagens, redirecionamentos ou chamadas de API.
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Tipo" secondary="Mensagem, Redirect ou API conforme a necessidade." /></ListItem>
            <ListItem><ListItemText primary="Gatilho e contexto" secondary="Defina quando e com quais dados a ação ocorre." /></ListItem>
            <ListItem><ListItemText primary="Bloqueio" secondary="Avalie se a ação deve interromper a conversa do usuário." /></ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Como melhorar</Typography>
          <Typography variant="body2">Comece simples. Monitore logs e ajuste os parâmetros de forma incremental.</Typography>
        </Box>
      );
    case 'schedule':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Defina janelas de funcionamento e condições especiais do atendimento randômico.
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Dias e horários" secondary="Mantenha alinhado ao horário comercial da equipe." /></ListItem>
            <ListItem><ListItemText primary="IA desabilitada" secondary="Pause a IA quando necessário (feriados, manutenção)." /></ListItem>
            <ListItem><ListItemText primary="Cliente assume" secondary="Permite que um humano conduza em períodos críticos." /></ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Como melhorar</Typography>
          <Typography variant="body2">Revise métricas de resposta por horário e ajuste as janelas para otimizar desempenho.</Typography>
        </Box>
      );
    case 'settings':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Ajuste roteamento, follow-up, integrações e políticas de operação.
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Roteamento" secondary="Determine quando o lead vai para humano ou continua na IA." /></ListItem>
            <ListItem><ListItemText primary="Follow-up" secondary="Envio automático de acompanhamento após X minutos/horas." /></ListItem>
            <ListItem><ListItemText primary="CRM" secondary="Ative e configure URL/Token para registrar leads automaticamente." /></ListItem>
            <ListItem><ListItemText primary="Políticas" secondary="Nome obrigatório, bloqueio de IA e modelo mais potente onde necessário." /></ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Como melhorar</Typography>
          <Typography variant="body2">Comece com políticas mínimas e evolua conforme feedback dos usuários e equipe.</Typography>
        </Box>
      );
    case 'review':
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Revise os dados e valide se o comportamento esperado foi atendido.
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Campos obrigatórios" secondary="Nome e descrição preenchidos e coerentes." /></ListItem>
            <ListItem><ListItemText primary="Prompts e ações" secondary="Sem sobreposições ou regras conflitantes." /></ListItem>
            <ListItem><ListItemText primary="Agendamentos e integrações" secondary="Horários corretos e CRM funcional." /></ListItem>
          </List>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Dica</Typography>
          <Typography variant="body2">Faça um teste completo de ponta-a-ponta antes de publicar.</Typography>
        </Box>
      );
    default:
      return null;
  }
};

export const AgentHelpModal: React.FC<AgentHelpModalProps> = ({ open, topic, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {titles[topic]}
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <ContentByTopic topic={topic} />
      </DialogContent>
    </Dialog>
  );
};

export default AgentHelpModal;


